import { IonIcon } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { closeOutline, searchOutline } from "ionicons/icons";

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  debounce?: number;
  className?: string;
}

export function SearchField({
  value,
  onChange,
  onBlur,
  placeholder = "Search...",
  autoFocus = false,
  debounce = 0,
  className = "",
}: SearchFieldProps) {
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Clear any pending debounce timer when value changes externally
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (debounce > 0) {
      debounceTimerRef.current = setTimeout(() => {
        onChange(newValue);
      }, debounce);
    } else {
      onChange(newValue);
    }
  };

  const handleClear = () => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    setInternalValue("");
    onChange("");
    inputRef.current?.focus();
  };

  return (
    <div
      className={`flex items-center gap-2 rounded-full border border-bordercolor-primary bg-white px-4 py-3 shadow-search ${className}`}
    >
      <IonIcon icon={searchOutline} className="size-4 shrink-0 text-txt-primary" />
      <input
        ref={inputRef}
        type="text"
        value={internalValue}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-base leading-6 text-txt-primary outline-none placeholder:text-txt-tertiary"
      />
      <button
        type="button"
        onClick={handleClear}
        aria-label={t("common:a11y.clear-search")}
        className="flex shrink-0 items-center justify-center transition-opacity active:opacity-70"
      >
        <IonIcon icon={closeOutline} className="size-6 text-txt-primary" />
      </button>
    </div>
  );
}
